/**
 * Where the microservice gateway is.
 *
 * NOT a constant. The gateway is called by the BROWSER, so a hardcoded origin only works for
 * whoever that origin belongs to — a localhost value cannot be shared, and a deployed value
 * cannot be tested against a local build.
 *
 * Resolution order:
 *   1. ?gateway=<origin> on the URL, remembered afterwards. This is what lets one shared link
 *      carry the gateway with it when the tunnel hostnames are unrelated.
 *   2. localStorage (from step 1, or set by hand).
 *   3. A VS Code dev tunnel — <label>-<port>.<region>.devtunnels.ms — where the gateway is the
 *      same tunnel on port 5500, so nothing needs configuring.
 *   4. localhost / 127.0.0.1 -> https://production-luqta.xtroedge.com, so local development works with no setup.
 *   5. Otherwise the deployed gateway.
 *
 * To point a build at the deployed gateway, set the ?gateway= parameter once, or change the
 * fallback below.
 */
const GATEWAY_STORAGE_KEY = 'luqta_gateway_origin';
function resolveGatewayOrigin(): string {
  const fallback = 'https://production-luqta.xtroedge.com';
  if (typeof window === 'undefined' || !window.location) return fallback;
  const trim = (u: string) => u.trim().replace(/\/+$/, '');
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('gateway');
    if (fromUrl) {
      const v = trim(fromUrl);
      try { window.localStorage.setItem(GATEWAY_STORAGE_KEY, v); } catch { /* private mode */ }
      return v;
    }
    // On localhost a remembered gateway is ignored — a stale one would silently send local
    // testing to a deployed server. Only an explicit ?gateway= overrides localhost:5500.
    if (/^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname)) return 'https://production-luqta.xtroedge.com';
    const saved = window.localStorage.getItem(GATEWAY_STORAGE_KEY);
    if (saved) return trim(saved);
  } catch {
    // A browser with site data blocked still has to reach a gateway — fall through.
  }
  const host = window.location.hostname;
  const m = host.match(/^(.*)-(\d+)\.(.+\.devtunnels\.ms)$/i);
  if (m) return `${window.location.protocol}//${m[1]}-5500.${m[3]}`;
  if (host === 'localhost' || host === '127.0.0.1') return 'https://production-luqta.xtroedge.com';
  return fallback;
}
const GATEWAY_ORIGIN = resolveGatewayOrigin();

// Development environment.
// Used by: `ng build --configuration=development`

export const environment = {
  production: false,
  aiChatboltBaseUrl: 'https://ai-dev-luqta.xtroedge.com',
  // ChatBolt AI-endpoint key for the AI image regenerate (same key as the business dashboard's chatboltKeys.endpoints)
  chatboltEndpointsKey: 'cb_2792471aa4694699a69ae6f6b8337138',
  businessBaseUrl: 'https://business-dev.luqta.io',
  contestAppUrl: 'https://contest-dev.luqta.io',
  // Base of the Web QR scan links (/q/<uuid>) — the QR codes always point here,
  // whatever base the report endpoint happens to return.
  webQrAppUrl: 'https://web-luqta.vercel.app',
  // Platform-webhook level completion, on the loyalty gateway.
  webhookPath: '/webhooks/luqta',
  // Origin of the micro-services gateway (also serves uploaded media and the webhooks).
  gatewayOrigin: GATEWAY_ORIGIN,
  // Loyalty micro-services gateway. Single POST /action endpoint.
  // Resolved above rather than fixed — see resolveGatewayOrigin(). The previous value
  // ('https://luqta-api.xtroedge.com/action') is still reachable by passing
  // ?gateway=https://luqta-api.xtroedge.com once.
  loyaltyApiUrl: GATEWAY_ORIGIN + '/action',
  // No service key lives here. Anything in this file ships in the browser bundle; sign-in
  // needs no key and every other call is authorised by the admin's session token.

};
